class TolstoySliderSection extends HTMLElement {
  connectedCallback() {
    if (this._ready) return

    customElements.whenDefined('splide-carousel').then(() => {
      this.waitForSliders()
    })
  }

  waitForSliders(attempts = 0) {
    this.carousel = this.querySelector('.tolstoy-section__slider')
    this.mainCarousel = this.querySelector('.tolstoy-section__overlay-slider')
    this.overlay = this.querySelector('.tolstoy-section__overlay')
    this.carouselSplide = this.carousel?.slider
    this.mainSplide = this.mainCarousel?.slider
    this.hasOverlay = this.dataset.noOverlay !== 'true'

    const slidersReady = this.carouselSplide && (!this.hasOverlay || this.mainSplide)

    if (slidersReady) {
      this.init()
      return
    }

    if (attempts < 20) {
      requestAnimationFrame(() => this.waitForSliders(attempts + 1))
    }
  }

  init() {
    if (this._ready) return

    this._ready = true
    this.muted = false
    this.isOpen = false
    this.activeDesktopPov = null

    this.getOverlayVideos().forEach((video) => {
      video.playsInline = true
      video.setAttribute('playsinline', '')
      video.setAttribute('webkit-playsinline', '')
    })

    this.getCarouselVideos().forEach((video) => {
      video.playsInline = true
      video.setAttribute('playsinline', '')
      video.setAttribute('webkit-playsinline', '')
    })

    this.bindPlayButtons()
    this.bindMainNav()
    this.bindSoundButtons()
    this.bindPlayPauseButtons()
    this.bindOverlayDismiss()
    this.bindAddToCart()
    this.bindViewportObserver()

    if (this.hasOverlay) {
      this.mainSplide.on('moved', () => {
        if (this.isOpen && !this._isOpening) {
          requestAnimationFrame(() => {
            this.playOverlaySlide()
            this.updateMainNavState()
          })
        }
      })
    }
  }

  isMobile() {
    return window.matchMedia('(max-width: 920px)').matches
  }

  bindPlayButtons() {
    this.querySelectorAll('[data-tolstoy-play]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault()
        event.stopPropagation()

        const index = Number(button.dataset.tolstoyOpen)

        if (this.isMobile()) {
          this.openOverlay(index)
          return
        }

        this.playDesktopSlide(button.closest('[data-tolstoy-video-pov]'))
      })
    })
  }

  bindMainNav() {
    this.overlay?.addEventListener('click', (event) => {
      if (event.target.closest('[data-main-prev]')) {
        this.mainSplide.go('<')
      }

      if (event.target.closest('[data-main-next]')) {
        this.mainSplide.go('>')
      }

      if (event.target.closest('[data-tolstoy-close]')) {
        this.closeOverlay()
      }
    })
  }

  bindSoundButtons() {
    this.overlay?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-sound]')
      if (!button) return

      event.preventDefault()
      event.stopPropagation()
      this.toggleSound(button)
    })

    this.addEventListener('click', (event) => {
      if (event.target.closest('.tolstoy-section__overlay')) return

      const button = event.target.closest('[data-sound]')
      if (!button) return

      event.preventDefault()
      event.stopPropagation()
      this.toggleSound(button)
    })
  }

  bindPlayPauseButtons() {
    this.addEventListener('click', (event) => {
      if (event.target.closest('.tolstoy-section__overlay')) return

      const button = event.target.closest('[data-playpause]')
      if (!button) return

      event.preventDefault()
      event.stopPropagation()

      const pov = button.closest('[data-tolstoy-video-pov]')
      if (pov) this.toggleDesktopPlayPause(pov)
    })
  }

  bindOverlayDismiss() {
    this.overlay?.addEventListener('click', (event) => {
      if (event.target === this.overlay) this.closeOverlay()
    })

    this._onKeydown = (event) => {
      if (event.key === 'Escape' && this.isOpen) this.closeOverlay()
    }

    document.addEventListener('keydown', this._onKeydown)
  }

  bindViewportObserver() {
    if (!('IntersectionObserver' in window)) return

    this._viewportObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) this.handleLeftViewport()
        })
      },
      { threshold: 0 }
    )

    const viewportTarget = this.carousel || this.querySelector('.tolstoy-section__slider') || this
    this._viewportObserver.observe(viewportTarget)
  }

  observeActiveDesktopSlide() {
    if (!('IntersectionObserver' in window)) return

    this.unobserveActiveDesktopSlide()

    if (this.isMobile() || !this.activeDesktopPov) return

    const slide =
      this.activeDesktopPov.closest('.splide__slide') ||
      this.activeDesktopPov.closest('.tolstoy-section__slide') ||
      this.activeDesktopPov

    if (!this._activeSlideObserver) {
      this._activeSlideObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) this.handleLeftViewport()
          })
        },
        { threshold: 0 }
      )
    }

    this._activeSlideTarget = slide
    this._activeSlideObserver.observe(slide)
  }

  unobserveActiveDesktopSlide() {
    if (this._activeSlideObserver && this._activeSlideTarget) {
      this._activeSlideObserver.unobserve(this._activeSlideTarget)
      this._activeSlideTarget = null
    }
  }

  handleLeftViewport() {
    if (this.isOpen) this.closeOverlay()
    this.stopAllCarouselVideos()
  }

  bindAddToCart() {
    this._atcResetTimers = new Map()
    this._checkmarkTemplate = this.querySelector('[data-tolstoy-atc-check-template]')

    this.querySelectorAll('product-form[data-tolstoy-atc]').forEach((form) => {
      form.addEventListener('tolstoy:added', () => {
        this.showAddedState(form)
      })
    })
  }

  showAddedState(productForm) {
    const button = productForm.querySelector('[data-tolstoy-atc-button]')
    const label = productForm.querySelector('[data-tolstoy-atc-label]')
    const icon = productForm.querySelector('[data-tolstoy-atc-icon]')

    if (!button || !label || !icon) return

    const existingTimer = this._atcResetTimers.get(button)
    if (existingTimer) clearTimeout(existingTimer)

    if (!button.dataset.tolstoyOriginalLabel) {
      button.dataset.tolstoyOriginalLabel = label.textContent.trim()
      button.dataset.tolstoyOriginalIcon = icon.innerHTML
    }

    button.setAttribute('aria-disabled', 'true')
    button.disabled = true
    button.classList.add('tolstoy-atc--added')
    label.textContent = this.dataset.addedLabel || 'Added to cart'

    icon.innerHTML = ''
    const checkmark = this._checkmarkTemplate?.content?.firstElementChild?.cloneNode(true)
    if (checkmark) icon.appendChild(checkmark)

    const resetTimer = setTimeout(() => {
      this.resetAddedState(button, label, icon)
      this._atcResetTimers.delete(button)
    }, 5000)

    this._atcResetTimers.set(button, resetTimer)
  }

  resetAddedState(button, label, icon) {
    button.removeAttribute('aria-disabled')
    button.disabled = false
    button.classList.remove('tolstoy-atc--added')
    label.textContent = button.dataset.tolstoyOriginalLabel || this.dataset.buyLabel || ''
    icon.innerHTML = button.dataset.tolstoyOriginalIcon || ''
  }

  getCarouselVideos() {
    return [...this.querySelectorAll('.tolstoy-section__video-pov video')]
  }

  getOverlayVideos() {
    if (!this.mainCarousel) return []
    return [...this.mainCarousel.querySelectorAll('video')]
  }

  getCarouselPovs() {
    return [...this.querySelectorAll('.tolstoy-section__video-pov')]
  }

  getOverlayPovs() {
    if (!this.mainCarousel) return []
    return [...this.mainCarousel.querySelectorAll('.tolstoy-section__overlay-video-pov')]
  }

  getActiveOverlaySlide() {
    if (!this.mainCarousel) return null

    return (
      this.mainCarousel.querySelector('.splide__slide.is-visible') ||
      this.mainCarousel.querySelector('.splide__slide.is-active:not([aria-hidden="true"])') ||
      null
    )
  }

  getSlideVideo(slide) {
    if (!slide) return null

    const video = slide.querySelector('video')
    if (!video) return null

    if (this.getVideoSourceUrl(video)) return video

    const logicalIndex = slide.dataset.tolstoySlide
    if (logicalIndex == null) return video

    const sourceSlide = this.mainCarousel.querySelector(
      `.splide__slide[data-tolstoy-slide="${logicalIndex}"]:not(.splide__slide--clone)`
    )
    const sourceVideo = sourceSlide?.querySelector('video')
    if (!sourceVideo || !this.getVideoSourceUrl(sourceVideo)) return video

    const sources = sourceVideo.querySelectorAll('source')

    if (sources.length) {
      video.replaceChildren(...[...sources].map((item) => item.cloneNode(true)))
    } else if (sourceVideo.src) {
      video.src = sourceVideo.src
    }

    if (sourceVideo.poster) video.poster = sourceVideo.poster
    video.load()

    return video
  }

  getVideoSourceUrl(video) {
    if (!video) return ''

    return (
      video.currentSrc ||
      video.querySelector('source')?.getAttribute('src') ||
      video.getAttribute('src') ||
      ''
    )
  }

  setVideoMuted(video, muted) {
    video.muted = muted
    video.defaultMuted = muted

    if (muted) {
      video.setAttribute('muted', '')
    } else {
      video.removeAttribute('muted')
    }
  }

  syncOverlayMutedState() {
    if (!this.mainCarousel) return

    this.getOverlayVideos().forEach((video) => {
      this.setVideoMuted(video, this.muted)
    })

    this.mainCarousel.querySelectorAll('[data-sound]').forEach((button) => {
      button.classList.toggle('sound-off', this.muted)
    })
  }

  syncDesktopMutedState(pov) {
    const video = pov?.querySelector('video')
    const button = pov?.querySelector('[data-sound]')

    if (video) this.setVideoMuted(video, this.muted)
    button?.classList.toggle('sound-off', this.muted)
  }

  resetVideo(video) {
    if (!video) return

    video.pause()
    video.load()
  }

  stopAllCarouselVideos() {
    this.unobserveActiveDesktopSlide()
    this._desktopPlaybackId = (this._desktopPlaybackId || 0) + 1

    this.getCarouselPovs().forEach((pov) => {
      pov.classList.remove('playing', 'init')
      pov.querySelector('[data-sound]')?.classList.remove('sound-off')
    })

    this.getCarouselVideos().forEach((video) => {
      this.resetVideo(video)
    })

    this.activeDesktopPov = null
    this.muted = false
  }

  playDesktopSlide(pov) {
    if (!pov) return

    const video = pov.querySelector('video')
    if (!video) return

    const playbackId = (this._desktopPlaybackId = (this._desktopPlaybackId || 0) + 1)

    this.getCarouselPovs().forEach((item) => {
      if (item !== pov) {
        item.classList.remove('playing', 'init')
        this.resetVideo(item.querySelector('video'))
      }
    })

    this.activeDesktopPov = pov
    pov.classList.add('playing', 'init')
    this.syncDesktopMutedState(pov)
    this.observeActiveDesktopSlide()

    const startPlayback = () => {
      if (this._desktopPlaybackId !== playbackId || this.activeDesktopPov !== pov) return
      video.volume = 1
      video.play().catch(() => {})
    }

    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      startPlayback()
      return
    }

    video.addEventListener('canplay', startPlayback, { once: true })

    if (video.networkState === HTMLMediaElement.NETWORK_EMPTY) {
      video.load()
    }
  }

  toggleDesktopPlayPause(pov) {
    const video = pov.querySelector('video')
    if (!video) return

    if (pov.classList.contains('playing')) {
      video.pause()
      pov.classList.remove('playing')
      this.unobserveActiveDesktopSlide()
      return
    }

    this.getCarouselPovs().forEach((item) => {
      if (item !== pov) {
        item.classList.remove('playing', 'init')
        this.resetVideo(item.querySelector('video'))
      }
    })

    this.activeDesktopPov = pov
    pov.classList.add('playing', 'init')
    this.syncDesktopMutedState(pov)
    this.observeActiveDesktopSlide()
    video.play().catch(() => {})
  }

  toggleSound(button) {
    const inOverlay = Boolean(button.closest('.tolstoy-section__overlay'))
    const pov = button.closest('[data-tolstoy-video-pov], .tolstoy-section__overlay-video-pov')

    if (inOverlay) {
      this.muted = !this.muted
      this.syncOverlayMutedState()

      if (!this.muted) {
        const video =
          this.getOverlayVideos().find((item) => !item.paused && !item.ended) ||
          this.getSlideVideo(this.getActiveOverlaySlide())

        if (video) {
          video.volume = 1
          video.play().catch(() => {})
        }
      }

      return
    }

    if (!pov) return

    this.muted = !this.muted
    this.syncDesktopMutedState(pov)

    if (!this.muted) {
      const video = pov.querySelector('video')
      if (video) {
        video.volume = 1
        video.play().catch(() => {})
      }
    }
  }

  goToOverlaySlide(index, animate = true) {
    const splide = this.mainSplide

    if (animate) {
      splide.go(index)
      return
    }

    const { Controller, Move, Transition, Slides } = splide.Components
    const { speed, waitForTransition } = splide.options

    Transition.cancel()
    splide.options.speed = 0
    splide.options.waitForTransition = false

    Controller.setIndex(index)
    Move.jump(index)
    Slides.update()

    splide.options.speed = speed
    splide.options.waitForTransition = waitForTransition
  }

  pauseAllOverlay() {
    this.getOverlayPovs().forEach((videoPov) => {
      videoPov.classList.remove('playing', 'init')
    })

    this.getOverlayVideos().forEach((video) => {
      this.resetVideo(video)
    })
  }

  playOverlaySlide() {
    this.pauseAllOverlay()

    const slide = this.getActiveOverlaySlide()
    const video = this.getSlideVideo(slide)

    if (!video) return

    const startPlayback = () => {
      if (this.getActiveOverlaySlide() !== slide) return

      this.setVideoMuted(video, this.muted)
      slide.querySelector('.tolstoy-section__overlay-video-pov')?.classList.add('playing', 'init')
      video.volume = 1
      video.play().catch(() => {})
    }

    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      startPlayback()
      return
    }

    video.addEventListener('canplay', startPlayback, { once: true })

    if (video.networkState === HTMLMediaElement.NETWORK_EMPTY) {
      video.load()
    }
  }

  mountOverlay() {
    if (this.overlay.parentElement !== document.body) {
      document.body.appendChild(this.overlay)
    }
  }

  unmountOverlay() {
    if (this.overlay.parentElement === document.body) {
      this.appendChild(this.overlay)
    }
  }

  openOverlay(index) {
    if (!this.hasOverlay) return

    this.stopAllCarouselVideos()
    this.isOpen = true
    this._isOpening = true
    this.muted = false
    this.syncOverlayMutedState()

    this.mountOverlay()
    this.overlay.classList.add('tolstoy-section__overlay--open')
    this.overlay.setAttribute('aria-hidden', 'false')
    this.classList.add('tolstoy-section--open')
    document.body.classList.add('tolstoy-section-open')

    requestAnimationFrame(() => {
      this.mainSplide.refresh()
      this.goToOverlaySlide(index, false)

      requestAnimationFrame(() => {
        this._isOpening = false
        this.playOverlaySlide()
        this.updateMainNavState()
      })
    })
  }

  closeOverlay() {
    this.isOpen = false
    this.pauseAllOverlay()
    this.getOverlayPovs().forEach((pov) => pov.classList.remove('init'))
    this.muted = false
    this.syncOverlayMutedState()
    this.overlay.classList.remove('tolstoy-section__overlay--open')
    this.overlay.setAttribute('aria-hidden', 'true')
    this.classList.remove('tolstoy-section--open')
    document.body.classList.remove('tolstoy-section-open')
    this.unmountOverlay()
    this.goToOverlaySlide(0, false)
  }

  updateMainNavState() {
    const prev = this.overlay?.querySelector('[data-main-prev]')
    const next = this.overlay?.querySelector('[data-main-next]')

    if (!prev || !next) return

    prev.disabled = false
    next.disabled = false
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onKeydown)
    this._viewportObserver?.disconnect()
    this._activeSlideObserver?.disconnect()

    this._atcResetTimers?.forEach((timer) => clearTimeout(timer))
    this._atcResetTimers?.clear()

    if (this.isOpen) {
      this.unmountOverlay()
    }

    document.body.classList.remove('tolstoy-section-open')
  }
}

customElements.define('tolstoy-slider-section', TolstoySliderSection)
